import { v4 as uuid } from 'uuid'

import { routes } from '@redwoodjs/router'

import UniversalSearchPlanPageResultBody from 'src/components/UniversalSearchModal/UniversalSearchPlanPageResultBody'
import {
  UniversalSearchOption,
  UniversalSearchOptionType,
} from 'src/components/UniversalSearchModal/util'

export type UniversalSearchPlanPageHit = {
  filePage?: { id: number } | null
  recordPlansSet?: { id: number } | null
  pageNumber: number
  pageTitle?: string | null
  fileName: string
  snippet: string
  recordUuid: string
  recordDisplayIdentifier: string
  isDraft: boolean
  organizationSlug?: string | null
}

type BuildPlanPageSearchOptionsArgs = {
  hits: UniversalSearchPlanPageHit[]
  searchQuery: string
}

/** Deep-link URL that opens plan review on a specific plan set version and page. */
export const buildPlanReviewFilePageUrl = (args: {
  recordUuid: string
  isDraft: boolean
  organizationSlug: string | null | undefined
  recordPlansSetId: number
  filePageId: number
  searchQuery?: string
}) => {
  const { recordUuid, isDraft, organizationSlug, recordPlansSetId, filePageId, searchQuery } = args
  const deepLinkParams = {
    openPlanReview: true,
    recordPlansSetId: `${recordPlansSetId}`,
    filePageId: `${filePageId}`,
    ...(searchQuery?.trim() ? { searchQuery: searchQuery.trim() } : {}),
  }
  if (isDraft) {
    return routes.editRecordDraft({
      recordUuid,
      slug: organizationSlug!,
      ...deepLinkParams,
    })
  }
  return routes.record({
    recordUuid,
    ...deepLinkParams,
  })
}

export const formatPlanPageDisplayTitle = (
  pageTitle: string | null | undefined,
  pageNumber: number
) => (pageTitle?.trim() ? pageTitle.trim() : `Page ${pageNumber + 1}`)

/** Title for a plan set page hit row in Universal Search. */
export const formatPlanSetPageSearchResultTitle = (args: {
  recordDisplayIdentifier: string
  fileName: string
  pageTitle: string | null | undefined
  pageNumber: number
}) => {
  const pageLabel = formatPlanPageDisplayTitle(args.pageTitle, args.pageNumber)
  return {
    pageLabel,
    title: `Plan set page · #${args.recordDisplayIdentifier} · ${args.fileName} · ${pageLabel}`,
  }
}

/** Maps OpenSearch plan set page hits to universal search modal options. */
export const buildPlanPageSearchOptions = ({
  hits,
  searchQuery,
}: BuildPlanPageSearchOptionsArgs): UniversalSearchOption[] =>
  hits.flatMap((hit) => {
    const filePageId = hit.filePage?.id
    const recordPlansSetId = hit.recordPlansSet?.id
    if (filePageId == null || recordPlansSetId == null) {
      return []
    }
    if (hit.isDraft && !hit.organizationSlug?.trim()) {
      return []
    }

    const url = buildPlanReviewFilePageUrl({
      recordUuid: hit.recordUuid,
      isDraft: hit.isDraft,
      organizationSlug: hit.organizationSlug,
      recordPlansSetId,
      filePageId,
      searchQuery,
    })
    const { pageLabel, title } = formatPlanSetPageSearchResultTitle({
      recordDisplayIdentifier: hit.recordDisplayIdentifier,
      fileName: hit.fileName,
      pageTitle: hit.pageTitle,
      pageNumber: hit.pageNumber,
    })
    return [
      {
        type: UniversalSearchOptionType.PlanPage,
        title,
        subtitle: <UniversalSearchPlanPageResultBody snippet={hit.snippet} />,
        subtitleSortString: hit.fileName + pageLabel + hit.snippet,
        url,
        uuid: uuid(),
      },
    ]
  })
